/**
 * SlotWizard — 4-step guided wizard for creating/editing a "cours fixe" (slot).
 *
 * Props:
 *   open          {boolean}
 *   onClose       {()=>void}
 *   contract      {Object}         full contract object (must include subject: {id, name})
 *   slot          {Object|null}    null = create mode; object = edit mode (prérempli)
 *   onSuccess     {()=>void}       called after create/edit success + delay
 *   showToast     {({message, variant})=>void}
 */

import { useState, useEffect, useRef } from 'react';
import {
  BookOpen,
  Calendar,
  Clock,
  CheckCircle,
  ChevronLeft,
} from 'lucide-react';
import Dialog from '../ui/Dialog';
import Button from '../ui/Button';
import Field from '../ui/Field';
import { getFieldErrors, getUserMessage } from '../../utils/apiErrors';
import apiService from '../../services/apiService';

// ─────────────────────────── constants ───────────────────────────

const STEPS = ['Matière', 'Jour', 'Horaires', 'Récapitulatif'];

const DAY_OPTIONS = [
  { value: 'monday',    short: 'Lu', label: 'Lundi' },
  { value: 'tuesday',   short: 'Ma', label: 'Mardi' },
  { value: 'wednesday', short: 'Me', label: 'Mercredi' },
  { value: 'thursday',  short: 'Je', label: 'Jeudi' },
  { value: 'friday',    short: 'Ve', label: 'Vendredi' },
  { value: 'saturday',  short: 'Sa', label: 'Samedi' },
  { value: 'sunday',    short: 'Di', label: 'Dimanche' },
];

// Map legacy numeric day_of_week → string value (for edit mode compat)
const NUMERIC_TO_STRING = {
  1: 'monday', 2: 'tuesday', 3: 'wednesday', 4: 'thursday',
  5: 'friday', 6: 'saturday', 7: 'sunday',
};

// ─────────────────────────── helpers ───────────────────────────

function calcDuration(start, end) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const total = (eh * 60 + em) - (sh * 60 + sm);
  if (total <= 0) return null;
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h${m < 10 ? '0' : ''}${m}`;
}

function dayLabel(value) {
  return DAY_OPTIONS.find((d) => d.value === value)?.label ?? value;
}

// ─────────────────────────── stepper ───────────────────────────

const Stepper = ({ current }) => (
  <div className="mb-6">
    <div className="flex gap-1 mb-2">
      {STEPS.map((_, i) => (
        <div
          key={i}
          className="flex-1 h-1 rounded-full transition-colors duration-300"
          style={{ backgroundColor: i <= current ? '#FFA726' : '#EAF4FB' }}
        />
      ))}
    </div>
    <div className="flex">
      {STEPS.map((label, i) => (
        <div key={i} className="flex-1 text-center">
          <span
            className="font-montserrat transition-colors duration-300"
            style={{
              fontSize: 10,
              color: i === current ? '#FFA726' : i < current ? '#4CE0C2' : '#9CA3AF',
              fontWeight: i === current ? 600 : 400,
            }}
          >
            {label}
          </span>
        </div>
      ))}
    </div>
  </div>
);

// ─────────────────────────── step slides ───────────────────────────

const SlideWrap = ({ children, direction }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const tx = direction === 'forward' ? 20 : -20;
    el.style.transform = `translateX(${tx}px)`;
    el.style.opacity = '0';
    requestAnimationFrame(() => {
      el.style.transition = 'transform 200ms ease, opacity 200ms ease';
      el.style.transform = 'translateX(0)';
      el.style.opacity = '1';
    });
  }, [direction]);

  return <div ref={ref}>{children}</div>;
};

// Step 1 — Matière
const Step1Subject = ({ subjects, selected, onSelect }) => {
  if (!subjects || subjects.length === 0) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
        <p className="text-sm font-montserrat text-red-700">
          Aucune matière associée à ce contrat. Complétez le contrat avant de programmer un cours.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-playfair font-bold text-[#17386E]">
          Quelle matière enseigner ?
        </h3>
        <p className="text-sm font-montserrat text-[#4A4A4A] mt-0.5">
          Choisissez la matière de ce cours fixe.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-2">
        {subjects.map((subj) => {
          const isSelected = selected?.id === subj.id;
          return (
            <button
              key={subj.id}
              type="button"
              onClick={() => onSelect(subj)}
              className="relative rounded-xl p-4 text-left transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#FFA726]"
              style={{
                backgroundColor: isSelected ? '#EAF4FB' : '#F5F5F5',
                border: isSelected ? '2px solid #FFA726' : '2px solid transparent',
              }}
            >
              {isSelected && (
                <span
                  className="absolute top-2 right-2"
                  style={{ color: '#4CE0C2' }}
                  aria-hidden="true"
                >
                  <CheckCircle size={16} />
                </span>
              )}
              <BookOpen
                size={18}
                className="mb-1"
                style={{ color: isSelected ? '#FFA726' : '#17386E' }}
              />
              <span className="block text-sm font-montserrat font-medium text-[#17386E]">
                {subj.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Step 2 — Jour
const Step2Day = ({ selected, onSelect }) => (
  <div className="space-y-3">
    <div>
      <h3 className="text-base font-playfair font-bold text-[#17386E]">
        Quel jour de la semaine ?
      </h3>
      <p className="text-sm font-montserrat text-[#4A4A4A] mt-0.5">
        Ce cours aura lieu chaque semaine ce jour-là.
      </p>
    </div>
    <div className="flex gap-2 flex-wrap mt-2">
      {DAY_OPTIONS.map((d) => {
        const isSelected = selected === d.value;
        return (
          <button
            key={d.value}
            type="button"
            onClick={() => onSelect(d.value)}
            aria-pressed={isSelected}
            className="font-montserrat text-sm font-semibold rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#FFA726]"
            style={{
              width: 44,
              height: 44,
              backgroundColor: isSelected ? '#FFF1DA' : '#F5F5F5',
              border: isSelected ? '2px solid #FFA726' : '2px solid transparent',
              color: isSelected ? '#FFA726' : '#4A4A4A',
            }}
          >
            {d.short}
          </button>
        );
      })}
    </div>
  </div>
);

// Step 3 — Horaires
const Step3Times = ({ startTime, endTime, onStartChange, onEndChange, timeError }) => {
  const duration = calcDuration(startTime, endTime);
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-playfair font-bold text-[#17386E]">
          À quelle heure ?
        </h3>
      </div>
      <Field
        label="Heure de début *"
        id="wizard-start"
        name="start_time"
        type="time"
        value={startTime}
        onChange={(e) => onStartChange(e.target.value)}
      />
      <Field
        label="Heure de fin *"
        id="wizard-end"
        name="end_time"
        type="time"
        value={endTime}
        onChange={(e) => onEndChange(e.target.value)}
        error={timeError}
      />
      {duration && !timeError && (
        <div className="flex items-center gap-1 text-[#4A4A4A]">
          <Clock size={12} />
          <span className="font-montserrat text-xs">Durée : {duration}</span>
        </div>
      )}
    </div>
  );
};

// Step 4 — Récapitulatif
const Step4Summary = ({ subject, day, startTime, endTime, isEdit, submitting, backendError, successState }) => {
  const duration = calcDuration(startTime, endTime);
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-playfair font-bold text-[#17386E]">
          Vérifiez avant de valider
        </h3>
      </div>

      {/* Recap card */}
      <div
        className="rounded-2xl p-4 space-y-3"
        style={{ backgroundColor: '#EAF4FB' }}
      >
        <div className="flex items-center gap-2">
          <BookOpen size={16} style={{ color: '#17386E' }} />
          <span className="font-montserrat text-sm text-[#17386E] font-medium">
            {subject?.name}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar size={16} style={{ color: '#17386E' }} />
          <span className="font-montserrat text-sm text-[#17386E]">
            {dayLabel(day)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Clock size={16} style={{ color: '#17386E' }} />
          <span className="font-montserrat text-sm text-[#17386E]">
            {startTime} – {endTime}
            {duration && <span className="ml-1 text-gray-500">({duration})</span>}
          </span>
        </div>

        {/* Success overlay inside recap card */}
        {successState && (
          <div className="mt-3 flex items-center gap-2 rounded-lg px-3 py-2 bg-green-50 border border-green-200">
            <CheckCircle size={16} style={{ color: '#4CE0C2' }} />
            <span className="font-montserrat text-sm text-green-700">
              {successState}
            </span>
          </div>
        )}
      </div>

      {/* Impact banner */}
      {!successState && (
        <div
          className="flex items-start gap-2 rounded-lg px-3 py-3"
          style={{
            backgroundColor: 'rgba(76,224,194,0.10)',
            border: '1px solid rgba(76,224,194,0.30)',
          }}
        >
          <CheckCircle size={16} style={{ color: '#4CE0C2', flexShrink: 0, marginTop: 2 }} />
          <p className="font-montserrat text-sm text-[#4A4A4A]">
            {isEdit
              ? 'Les séances à venir seront mises à jour. Les séances passées ne changent pas.'
              : 'Les séances des 4 prochaines semaines seront créées automatiquement. Le parent sera notifié.'}
          </p>
        </div>
      )}

      {/* Backend error */}
      {backendError && (
        <div className="bg-red-50 rounded-lg px-3 py-2 text-sm text-red-600 font-montserrat">
          {backendError}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────── main component ───────────────────────────

const SlotWizard = ({ open, onClose, contract, slot, onSuccess, showToast }) => {
  const isEdit = Boolean(slot);

  // Derive subjects from contract (single subject wrapped in array for genericity)
  const subjects = contract?.subject
    ? [contract.subject]
    : [];

  // Wizard state
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState('forward');
  const [subject, setSubject] = useState(null);
  const [day, setDay] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [timeError, setTimeError] = useState('');
  const [backendError, setBackendError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successState, setSuccessState] = useState(null); // string message on success

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingSlot, setDeletingSlot] = useState(false);

  // Reset on open
  useEffect(() => {
    if (!open) return;

    setBackendError('');
    setTimeError('');
    setSuccessState(null);
    setSubmitting(false);

    if (slot) {
      // Edit mode — prefill
      const dayVal = typeof slot.day_of_week === 'number'
        ? NUMERIC_TO_STRING[slot.day_of_week]
        : (slot.day_of_week ?? '');

      const slotSubject = slot.subject ?? contract?.subject ?? null;
      setSubject(slotSubject);
      setDay(dayVal);
      setStartTime(slot.start_time ? slot.start_time.slice(0, 5) : '');
      setEndTime(slot.end_time ? slot.end_time.slice(0, 5) : '');
      setStep(0);
    } else {
      // Create mode
      const autoSubject = subjects.length === 1 ? subjects[0] : null;
      setSubject(autoSubject);
      setDay('');
      setStartTime('');
      setEndTime('');
      // Auto-skip step 1 if single subject
      setStep(subjects.length === 1 ? 1 : 0);
    }
  }, [open, slot]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── navigation ──

  const goNext = () => {
    setDirection('forward');
    setStep((s) => s + 1);
  };

  const goBack = () => {
    setDirection('backward');
    setBackendError('');
    setStep((s) => s - 1);
  };

  // ── step validity ──

  const stepValid = () => {
    if (step === 0) return Boolean(subject);
    if (step === 1) return Boolean(day);
    if (step === 2) {
      if (!startTime || !endTime) return false;
      if (startTime >= endTime) return false;
      return true;
    }
    return true;
  };

  // ── time validation inline ──

  const handleEndTimeChange = (val) => {
    setEndTime(val);
    if (startTime && val && startTime >= val) {
      setTimeError("L'heure de fin doit être après l'heure de début.");
    } else {
      setTimeError('');
    }
  };

  const handleStartTimeChange = (val) => {
    setStartTime(val);
    if (val && endTime && val >= endTime) {
      setTimeError("L'heure de fin doit être après l'heure de début.");
    } else {
      setTimeError('');
    }
  };

  // ── submit ──

  const handleSubmit = async () => {
    setSubmitting(true);
    setBackendError('');

    const payload = {
      subject_id: subject.id,
      day_of_week: day,
      start_time: startTime,
      end_time: endTime,
    };

    try {
      let result;
      if (isEdit) {
        result = await apiService.updateSlot(contract.id, slot.id, payload);
      } else {
        result = await apiService.createSlot(contract.id, payload);
      }

      const count = result?.upcoming_sessions_count ?? result?.data?.upcoming_sessions_count;
      const dayName = dayLabel(day);

      const successMsg = isEdit
        ? 'Modifications enregistrées.'
        : `Cours enregistré.${count != null ? ` ${count} séance${count > 1 ? 's' : ''} programmée${count > 1 ? 's' : ''}.` : ''}`;

      setSuccessState(successMsg);
      showToast({ message: `Cours du ${dayName} enregistré.`, variant: 'success' });

      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      const fieldErrors = getFieldErrors(err);
      // start_time / overlap → retour à l'étape 3
      const msg =
        fieldErrors.start_time ||
        fieldErrors.end_time ||
        fieldErrors.overlap ||
        fieldErrors.day_of_week ||
        null;

      if (msg) {
        setBackendError(msg);
        // If it's a time/overlap error, go back to step 3 (index 2)
        if (fieldErrors.start_time || fieldErrors.overlap) {
          setDirection('backward');
          setStep(2);
        }
      } else {
        showToast({ message: getUserMessage(err), variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── delete ──

  const handleDelete = async () => {
    setDeletingSlot(true);
    try {
      await apiService.deleteSlot(contract.id, slot.id);
      showToast({ message: 'Cours supprimé.', variant: 'success' });
      setDeleteConfirmOpen(false);
      onSuccess();
      onClose();
    } catch (err) {
      showToast({ message: getUserMessage(err), variant: 'error' });
    } finally {
      setDeletingSlot(false);
    }
  };

  // ── render ──

  const canContinue = stepValid();
  const isLastStep = step === 3;

  const stepContent = (
    <SlideWrap key={step} direction={direction}>
      {step === 0 && (
        <Step1Subject
          subjects={subjects}
          selected={subject}
          onSelect={(subj) => {
            setSubject(subj);
          }}
        />
      )}
      {step === 1 && (
        <Step2Day selected={day} onSelect={setDay} />
      )}
      {step === 2 && (
        <Step3Times
          startTime={startTime}
          endTime={endTime}
          onStartChange={handleStartTimeChange}
          onEndChange={handleEndTimeChange}
          timeError={timeError}
        />
      )}
      {step === 3 && (
        <Step4Summary
          subject={subject}
          day={day}
          startTime={startTime}
          endTime={endTime}
          isEdit={isEdit}
          submitting={submitting}
          backendError={backendError}
          successState={successState}
        />
      )}
    </SlideWrap>
  );

  // Determine the minimum allowed back step (skip step 1 back if single subject + create)
  const minStep = !isEdit && subjects.length === 1 ? 1 : 0;

  const upcomingCount = slot?.upcoming_sessions_count;

  return (
    <>
      <Dialog
        open={open && !deleteConfirmOpen}
        onClose={onClose}
        title={isEdit ? 'Modifier ce cours' : 'Nouveau cours fixe'}
        className="max-w-lg"
      >
        <Stepper current={step} />

        {/* No subjects guard — show blocked state on step 0 */}
        {step === 0 && subjects.length === 0 ? (
          <div className="space-y-4">
            <Step1Subject subjects={[]} selected={null} onSelect={() => {}} />
            <div className="flex justify-end pt-2 border-t border-gray-100">
              <Button variant="ghost" size="sm" onClick={onClose}>
                Fermer
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {stepContent}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2">
                {step > minStep && !successState && (
                  <Button variant="ghost" size="sm" onClick={goBack} disabled={submitting}>
                    <ChevronLeft size={14} className="mr-1" />
                    Retour
                  </Button>
                )}
                {isEdit && !successState && (
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={submitting}
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    Supprimer
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-2">
                {!isLastStep && !successState && (
                  <>
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
                      Annuler
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!canContinue || submitting}
                      onClick={goNext}
                    >
                      Continuer
                    </Button>
                  </>
                )}

                {isLastStep && !successState && (
                  <>
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
                      Annuler
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={submitting}
                      onClick={handleSubmit}
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Enregistrement…
                        </span>
                      ) : isEdit ? (
                        'Enregistrer les modifications'
                      ) : (
                        'Programmer ce cours'
                      )}
                    </Button>
                  </>
                )}

                {successState && (
                  <Button variant="ghost" size="sm" onClick={onClose}>
                    Fermer
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Supprimer ce cours fixe"
      >
        <div className="space-y-4">
          <p className="text-sm font-montserrat text-[#4A4A4A]">
            Supprimer ce cours du{' '}
            <strong>{dayLabel(day)}</strong>{' '}
            {upcomingCount != null
              ? `annulera ${upcomingCount} séance${upcomingCount > 1 ? 's' : ''} à venir.`
              : 'annulera les séances à venir.'}{' '}
            Le parent sera informé. Cette action est irréversible.
          </p>
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              disabled={deletingSlot}
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Annuler
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={deletingSlot}
              onClick={handleDelete}
            >
              {deletingSlot ? 'Suppression…' : 'Oui, supprimer ce cours'}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default SlotWizard;
